// client/src/components/pages/WeighbridgeCompliance/ComplianceDashboard.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-3 / WBE-9: the KPIs, the headline, the Compliance Overview badges and the
// Upcoming Expirations list all come from the server's one compliance definition
// (GET /assets compliance_status + GET /assets/dashboard/stats). Before, the KPI
// required a verification record no screen could write, the overview recomputed
// a status from next_cert_due_date, and the expirations table read fields the
// calibrations list never had, so the three disagreed.
import { useState, useEffect, type ComponentType } from 'react';
import {
  Scale,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  AlertCircle,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import type { AxiosResponse } from 'axios';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import type {
  ActivitySourceRow,
  ComplianceAssetRow,
  ComplianceDashboardStats,
  ComplianceStatus,
  NcrStats,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

type KpiVariant = 'blue' | 'emerald' | 'amber' | 'red';

interface KPICardProps {
  label: string;
  value: number;
  icon: LucideIcon;
  variant: KpiVariant;
}

interface ActivityItem {
  id: string;
  type: 'calibration' | 'maintenance' | 'ncr';
  icon: ComponentType<{ className?: string }>;
  message: string;
  date: string | null | undefined;
  color: string;
}

type OverviewFilter = 'all' | 'compliant' | 'expiring' | 'non-compliant';

const OVERVIEW_FILTERS: Array<{ value: OverviewFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'compliant', label: 'Compliant' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'non-compliant', label: 'At Risk' },
];

/** Does an asset's server verdict fall under an overview filter? ('At Risk' = expired or non-compliant.) */
export function matchesOverviewFilter(
  status: ComplianceStatus | null | undefined,
  filter: OverviewFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'non-compliant') return status === 'expired' || status === 'non-compliant';
  return status === filter;
}

/** The status of one listed certificate from its days remaining (same thresholds as the server). */
export function certificateStatus(days: number | null | undefined): ComplianceStatus {
  if (days === null || days === undefined) return 'non-compliant';
  if (days <= 0) return 'expired';
  if (days <= 90) return 'expiring';
  return 'compliant';
}

/** Rows of a paginated `{ data }` body or a bare array. */
const listOf = <T,>(res: AxiosResponse<PaginatedBody<T> | T[]>): T[] => {
  const d = res.data;
  return Array.isArray(d) ? d : d?.data || [];
};

const KPICard = ({ label, value, icon: Icon, variant }: KPICardProps) => {
  const configs: Record<KpiVariant, { bg: string; border: string; accent: string }> = {
    blue: { bg: 'bg-blue-50/50', border: 'border-blue-100', accent: 'bg-blue-500' },
    emerald: { bg: 'bg-emerald-50/50', border: 'border-emerald-100', accent: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50/50', border: 'border-amber-100', accent: 'bg-amber-500' },
    red: { bg: 'bg-red-50/50', border: 'border-red-100', accent: 'bg-red-500' },
  };
  const config = configs[variant] || configs.blue;

  return (
    <div
      className={`relative rounded-xl border ${config.border} ${config.bg} p-5 hover:shadow-card-hover transition-all`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
            {label}
          </p>
          <p
            className="text-3xl font-bold tabular-nums"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            {value}
          </p>
        </div>
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${config.accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
};

interface BadgeConfig {
  bg: string;
  text: string;
  border: string;
  dot: string;
  label: string;
}

/** One badge for the four statuses of the compliance definition. */
export const StatusBadge = ({
  status,
  title,
}: {
  status: ComplianceStatus | null | undefined;
  title?: string | null;
}) => {
  const configs: Record<ComplianceStatus, BadgeConfig> = {
    compliant: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500',
      label: 'Compliant',
    },
    expiring: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
      label: 'Expiring Soon',
    },
    expired: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      dot: 'bg-red-500',
      label: 'Expired',
    },
    'non-compliant': {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      dot: 'bg-red-500',
      label: 'Non-Compliant',
    },
  };
  // No verdict means no valid certificate: never show it as Compliant.
  const config = (status && configs[status]) || configs['non-compliant'];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs ${config.bg} ${config.text} ${config.border}`}
      title={title || undefined}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const ComplianceDashboard = () => {
  const [stats, setStats] = useState<ComplianceDashboardStats | null>(null);
  const [ncrStats, setNcrStats] = useState<NcrStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [allAssets, setAllAssets] = useState<ComplianceAssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [overviewFilter, setOverviewFilter] = useState<OverviewFilter>('all');
  const [overviewSearch, setOverviewSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const [statsRes, ncrStatsRes, calibrationsRes, assetsRes, maintenanceRes, recentNcrRes] =
          await Promise.all([
            api.assets.getDashboardStats() as Promise<AxiosResponse<ComplianceDashboardStats>>,
            api.ncr.getStats() as Promise<AxiosResponse<NcrStats>>,
            // Newest certificates for the activity feed (the expirations table reads
            // stats.upcoming_expirations).
            api.calibrations.getAll({ limit: 5 }) as Promise<
              AxiosResponse<PaginatedBody<ActivitySourceRow> | ActivitySourceRow[]>
            >,
            // The overview lists every asset (the server caps a page at 100).
            api.assets.getAll({ limit: 100 }) as Promise<
              AxiosResponse<PaginatedBody<ComplianceAssetRow> | ComplianceAssetRow[]>
            >,
            api.maintenance.getAll({ limit: 10, sort: '-completedDate' }) as Promise<
              AxiosResponse<PaginatedBody<ActivitySourceRow> | ActivitySourceRow[]>
            >,
            api.ncr.getAll({ limit: 5 }) as Promise<
              AxiosResponse<PaginatedBody<ActivitySourceRow> | ActivitySourceRow[]>
            >,
          ]);

        if (cancelled) return;

        setStats(statsRes.data);
        setNcrStats(ncrStatsRes.data);
        setAllAssets(listOf(assetsRes));

        const activities: ActivityItem[] = [];
        listOf(calibrationsRes)
          .slice(0, 5)
          .forEach((cal) => {
            activities.push({
              id: `cal-${cal.id}`,
              type: 'calibration',
              icon: ShieldCheck,
              message: `${cal.calibration_type || 'Calibration'} certificate for ${cal.asset_name || 'asset'}`,
              date: cal.issue_date,
              color: 'text-emerald-500',
            });
          });

        listOf(maintenanceRes)
          .slice(0, 5)
          .forEach((m) => {
            activities.push({
              id: `maint-${m.id}`,
              type: 'maintenance',
              icon: Wrench,
              message: `${m.maintenance_type || 'Maintenance'} on ${m.asset_name || 'asset'}`,
              date: m.completed_date || m.scheduled_date,
              color: 'text-blue-500',
            });
          });

        listOf(recentNcrRes)
          .slice(0, 3)
          .forEach((n) => {
            activities.push({
              id: `ncr-${n.id}`,
              type: 'ncr',
              icon: AlertCircle,
              message: `NCR ${n.ncr_number || ''}: ${n.title || 'Non-conformance'} — ${n.asset_name || 'asset'}`,
              date: n.created_at,
              color: n.severity === 'Critical' ? 'text-red-500' : 'text-amber-500',
            });
          });

        activities.sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });

        setRecentActivity(activities.slice(0, 10));
      } catch {
        if (!cancelled) toast.error('Failed to load dashboard data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const getDaysColor = (days: number | null | undefined): string => {
    if (days === null || days === undefined) return 'text-slate-400';
    if (days <= 0) return 'text-red-600 font-semibold';
    if (days <= 30) return 'text-red-500';
    if (days <= 60) return 'text-amber-600';
    if (days <= 90) return 'text-amber-500';
    return 'text-emerald-600';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse rounded-xl border border-slate-200 p-5">
              <div className="h-4 bg-slate-200 rounded w-1/2 mb-3" />
              <div className="h-8 bg-slate-200 rounded w-1/3" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="animate-pulse bg-surface rounded-xl border border-slate-200 p-6">
            <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 bg-slate-200 rounded w-full" />
              ))}
            </div>
          </div>
          <div className="animate-pulse bg-surface rounded-xl border border-slate-200 p-6">
            <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 bg-slate-200 rounded w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalAssets = stats?.total_assets || 0;
  const totalActive = stats?.total_active || 0;
  const compliant = stats?.compliant || 0;
  const expiringSoon = stats?.expiring_soon || 0;
  const nonCompliant = stats?.non_compliant || 0;
  // Only active weighbridges need certificates, so the rate is over the active ones.
  const compliancePctNum = totalActive > 0 ? (compliant / totalActive) * 100 : 0;
  const compliancePct = totalActive > 0 ? compliancePctNum.toFixed(1) : '0';
  const expirations = stats?.upcoming_expirations || [];

  const openNcrs = (ncrStats?.open_count || 0) + (ncrStats?.in_progress_count || 0);
  const overdueNcrs = ncrStats?.overdue_count || 0;

  return (
    <div className="space-y-6">
      {/* Compliance headline */}
      <div className="flex items-center gap-4 bg-surface rounded-xl border border-slate-200 px-6 py-4">
        <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary-50">
          <Activity className="w-7 h-7 text-primary-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
            Overall Compliance Rate
          </p>
          <div className="flex items-baseline gap-3 mt-1">
            <span
              className="text-4xl font-bold tabular-nums"
              style={{
                fontFamily: 'IBM Plex Mono, monospace',
                color:
                  compliancePctNum >= 80
                    ? 'var(--success)'
                    : compliancePctNum >= 50
                      ? 'var(--warning)'
                      : 'var(--danger)',
              }}
            >
              {compliancePct}%
            </span>
            <span className="text-sm text-slate-400">
              {compliant} of {totalActive} active assets with valid certificates
            </span>
          </div>
          <div
            className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"
            style={{ maxWidth: '400px' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${compliancePct}%`,
                backgroundColor:
                  compliancePctNum >= 80
                    ? 'var(--success)'
                    : compliancePctNum >= 50
                      ? 'var(--warning)'
                      : 'var(--danger)',
              }}
            />
          </div>
        </div>
      </div>

      {/* KPI cards — 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Total Assets" value={totalAssets} icon={Scale} variant="blue" />
        <KPICard label="Compliant" value={compliant} icon={CheckCircle} variant="emerald" />
        <KPICard label="Expiring Soon" value={expiringSoon} icon={AlertTriangle} variant="amber" />
        <KPICard label="Non-Compliant" value={nonCompliant} icon={ShieldAlert} variant="red" />
        <KPICard
          label="Open NCRs"
          value={openNcrs}
          icon={AlertCircle}
          variant={openNcrs > 0 ? 'amber' : 'blue'}
        />
        <KPICard
          label="Overdue NCRs"
          value={overdueNcrs}
          icon={Clock}
          variant={overdueNcrs > 0 ? 'red' : 'blue'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3
              className="text-sm font-bold text-slate-800"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Upcoming Expirations
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Expired or expiring within 90 days</p>
          </div>
          {expirations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                <ShieldCheck className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-base font-semibold text-slate-700 mb-1">
                All certificates current
              </h3>
              <p className="text-sm text-slate-400 max-w-sm">
                No certificates are expiring in the next 90 days
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100">
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Asset
                    </th>
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Type
                    </th>
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Expiry
                    </th>
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Days
                    </th>
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {expirations.map((item) => {
                    const days = item.days_remaining;
                    return (
                      <tr
                        key={`${item.type}-${item.id}-${item.certificate_number ?? ''}`}
                        className="hover:bg-primary-50/30 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">
                          {item.asset_name || '-'}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600">
                          {item.certificate_type || '-'}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600">
                          {item.expiry_date ? formatAppDate(item.expiry_date) : '-'}
                        </td>
                        <td className={`px-5 py-3.5 text-sm font-medium ${getDaysColor(days)}`}>
                          {days !== null && days !== undefined
                            ? days <= 0
                              ? `${Math.abs(days)}d overdue`
                              : `${days}d`
                            : '-'}
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={certificateStatus(days)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3
              className="text-sm font-bold text-slate-800"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Recent Activity
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Latest calibrations, maintenance, and uploads
            </p>
          </div>
          {recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-base font-semibold text-slate-700 mb-1">No recent activity</h3>
              <p className="text-sm text-slate-400 max-w-sm">
                Activity will appear here as records are added
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="px-5 py-3.5 flex items-start gap-3 hover:bg-slate-50/50 transition-colors"
                >
                  <div className={`mt-0.5 ${activity.color}`}>
                    <activity.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{activity.message}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {activity.date ? formatAppDate(activity.date) : '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3
              className="text-sm font-bold text-slate-800"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Compliance Overview
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Current compliance status for all assets
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={overviewSearch}
              onChange={(e) => setOverviewSearch(e.target.value)}
              placeholder="Search assets..."
              className="w-44 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
            />
            <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
              {OVERVIEW_FILTERS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setOverviewFilter(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    overviewFilter === opt.value
                      ? 'bg-teal-500 text-white'
                      : 'bg-surface text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {allAssets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
              <Scale className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No assets registered</h3>
            <p className="text-sm text-slate-400 max-w-sm">
              Add weighbridge assets to track their compliance status
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Asset Name
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Location
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Last Cert Date
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Next Due
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Compliance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allAssets
                  .filter((asset) => {
                    if (overviewSearch) {
                      const q = overviewSearch.toLowerCase();
                      if (
                        !(asset.name || '').toLowerCase().includes(q) &&
                        !(asset.location_name || '').toLowerCase().includes(q)
                      )
                        return false;
                    }
                    return matchesOverviewFilter(asset.compliance_status, overviewFilter);
                  })
                  .map((asset) => {
                    return (
                      <tr key={asset.id} className="hover:bg-primary-50/30 transition-colors">
                        <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">
                          {asset.name}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600">
                          {asset.location_name || '-'}
                        </td>
                        <td className="px-5 py-3.5">
                          {asset.status === 'Active' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Active
                            </span>
                          ) : asset.status === 'Out of Service' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs bg-amber-50 text-amber-700 border-amber-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                              Out of Service
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs bg-slate-100 text-slate-500 border-slate-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              {asset.status || 'Unknown'}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600">
                          {asset.last_cert_date ? formatAppDate(asset.last_cert_date) : '-'}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-600">
                          {asset.next_cert_due_date ? formatAppDate(asset.next_cert_due_date) : '-'}
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge
                            status={asset.compliance_status}
                            title={asset.compliance_note}
                          />
                          {asset.compliance_note && (
                            <p className="text-[11px] text-slate-400 mt-1">
                              {asset.compliance_note}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;
